import React, { useState, useMemo } from "react";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Navbar from "components/Navbar";
import Sidebar from "components/Sidebar";
import { useGetUserQuery } from "state/api";
import { PageTitleContext } from "context/PageTitleContext";
import { ViewAsContext } from "context/ViewAsContext";
import { canAccess, ROLE_LABELS } from "config/roles";

// Shown when the (effective) role may not open the current route.
const NoAccess = ({ role }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        textAlign: "center",
        p: 4,
        minHeight: "70vh",
        color: "text.secondary",
      }}
    >
      <LockOutlined sx={{ fontSize: 48, color: "text.disabled" }} />
      <Typography variant="h5" color="text.primary">
        Not available for your role
      </Typography>
      <Typography variant="body2">
        The {ROLE_LABELS[role] || role} role doesn't have access to this page.
      </Typography>
      <Button variant="contained" sx={{ mt: 1 }} onClick={() => navigate("/dashboard")}>
        Go to dashboard
      </Button>
    </Box>
  );
};

const Layout = () => {
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [pageTitle, setPageTitle] = useState("");
  const [viewRole, setViewRole] = useState(null);
  const { pathname } = useLocation();
  const { data } = useGetUserQuery();

  const realRole = data?.role || null;
  // Only a real admin may preview another role.
  const effectiveRole = realRole === "admin" && viewRole ? viewRole : realRole;

  const viewAsValue = useMemo(
    () => ({ realRole, viewRole, effectiveRole, setViewRole }),
    [realRole, viewRole, effectiveRole]
  );

  const allowed = canAccess(effectiveRole, pathname);

  return (
    <ViewAsContext.Provider value={viewAsValue}>
      <PageTitleContext.Provider value={{ title: pageTitle, setTitle: setPageTitle }}>
        <Box display={isNonMobile ? "flex" : "block"} width="100%" height="100%">
          <Sidebar
            user={data || {}}
            effectiveRole={effectiveRole}
            isNonMobile={isNonMobile}
            drawerWidth="250px"
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
          <Box flexGrow={1} minWidth={0}>
            <Navbar
              user={data || {}}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
            />
            {/* Guard every route by the effective role (real, or admin's preview) */}
            {realRole && !allowed ? <NoAccess role={effectiveRole} /> : <Outlet />}
          </Box>
        </Box>
      </PageTitleContext.Provider>
    </ViewAsContext.Provider>
  );
};

export default Layout;
